import { Camera, FileText, FileUp, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CameraViewfinder } from '@/components/documents/CameraViewfinder';
import { CaptureGuidance } from '@/components/documents/CaptureGuidance';
import { UploadProgress } from '@/components/documents/UploadProgress';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { renderPdfThumbnail } from '@/lib/pdfThumbnail';
import { toast } from '@/lib/toast';
import { useDocumentStore } from '@/store/documentStore';

// TODO: In the future, add SVG example illustrations per document type

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<boolean>;
  documentType: string;
  acceptedMimeTypes: string;
  maxSizeMb: number;
}

type ModalState = 'select' | 'camera' | 'preview' | 'uploading';

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function validateMimeType(file: File, acceptedMimeTypes: string): boolean {
  const accepted = acceptedMimeTypes.split(',').map((s) => s.trim());

  for (const pattern of accepted) {
    // Wildcard pattern like "image/*"
    if (pattern.endsWith('/*')) {
      const prefix = pattern.slice(0, -1);
      if (file.type.startsWith(prefix)) {
        return true;
      }
    }

    // Extension pattern like ".pdf"
    if (pattern.startsWith('.')) {
      const ext = file.name.toLowerCase().split('.').pop();
      if (ext && `.${ext}` === pattern.toLowerCase()) {
        return true;
      }
    }

    // Exact MIME type
    if (file.type === pattern) {
      return true;
    }
  }

  return false;
}

export function UploadModal({
  isOpen,
  onClose,
  onUpload,
  documentType,
  acceptedMimeTypes,
  maxSizeMb,
}: UploadModalProps) {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<ModalState>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadProgress = useDocumentStore((state) => state.uploadProgress);
  const uploadPhase = useDocumentStore((state) => state.uploadPhase);
  const uploadError = useDocumentStore((state) => state.uploadError);
  const cancelUpload = useDocumentStore((state) => state.cancelUpload);

  const isUploading = modalState === 'uploading';
  const dialogRef = useFocusTrap(isOpen);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setModalState('select');
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    onClose();
  }, [isUploading, onClose]);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }

    return undefined;
  }, [isOpen, handleEscape]);

  const validateFile = (file: File): boolean => {
    if (!validateMimeType(file, acceptedMimeTypes)) {
      toast.error(t('documents.modal.invalidType'));
      return false;
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(t('documents.modal.fileTooLarge', { max: maxSizeMb }));
      return false;
    }

    return true;
  };

  const handleFileSelected = (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    // Clean up previous preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);

    if (isImageFile(file)) {
      setPreviewUrl(URL.createObjectURL(file));
      setModalState('preview');
    } else if (file.type === 'application/pdf') {
      setModalState('preview');
      void renderPdfThumbnail(file).then((thumbnailUrl) => {
        if (thumbnailUrl) {
          setPreviewUrl(thumbnailUrl);
        }
      });
    } else {
      setPreviewUrl(null);
      setModalState('preview');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    setModalState('uploading');
    const success = await onUpload(selectedFile);
    if (success) {
      onClose();
    }
    // On failure, stay in 'uploading' state — UploadProgress shows error+retry
  };

  const handleCancelUpload = () => {
    cancelUpload();
    setModalState('preview');
  };

  const handleRetryUpload = () => {
    if (!selectedFile) return;

    void onUpload(selectedFile).then((success) => {
      if (success) {
        onClose();
      }
    });
  };

  const handleChooseAnother = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setModalState('select');
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleCameraCapture = (file: File) => {
    handleFileSelected(file);
  };

  return (
    <>
      {/* Camera viewfinder (fullscreen overlay) */}
      {modalState === 'camera' && (
        <CameraViewfinder
          documentType={documentType}
          onCapture={handleCameraCapture}
          onClose={() => setModalState('select')}
        />
      )}

      <div
        className='fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50'
        onClick={handleOverlayClick}>
        <div
          ref={dialogRef}
          role='dialog'
          aria-modal='true'
          aria-labelledby='upload-modal-title'
          className='bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl shadow-xl max-h-[90vh] overflow-y-auto'>
          {/* Header */}
          <div className='flex items-center justify-between p-4 border-b border-gray-200'>
            <h3 id='upload-modal-title' className='text-lg font-semibold text-gray-900'>
              {t('documents.modal.title', { documentType })}
            </h3>
            <button
              type='button'
              onClick={handleClose}
              disabled={isUploading}
              aria-label={t('common.close')}
              className='p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed'>
              <X size={20} aria-hidden='true' />
            </button>
          </div>

          {/* Body */}
          <div className='p-4 space-y-4'>
            {modalState === 'select' && (
              <>
                <CaptureGuidance />

                <div className='flex flex-col gap-3'>
                  {/* Camera button — hidden on desktop */}
                  <button
                    type='button'
                    onClick={() => setModalState('camera')}
                    className='sm:hidden flex items-center justify-center gap-2 w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition'>
                    <Camera size={20} />
                    {t('documents.modal.takePhoto')}
                  </button>

                  {/* File picker button */}
                  <button
                    type='button'
                    onClick={() => fileInputRef.current?.click()}
                    className='flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-gray-300 text-gray-700 rounded-lg font-medium hover:border-primary hover:text-primary transition'>
                    <FileUp size={20} />
                    {t('documents.modal.chooseFile')}
                  </button>
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type='file'
                  accept={acceptedMimeTypes}
                  className='hidden'
                  tabIndex={-1}
                  onChange={handleInputChange}
                />
              </>
            )}

            {modalState === 'preview' && selectedFile && (
              <>
                {/* Preview area */}
                <div className='flex flex-col items-center gap-3'>
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={selectedFile.name}
                      className='w-full max-h-64 object-contain rounded-lg border border-gray-200'
                    />
                  ) : selectedFile.type === 'application/pdf' ? (
                    <div className='flex flex-col items-center gap-2 py-8'>
                      <div
                        role='status'
                        aria-label={t('documents.modal.generatingPreview')}
                        className='h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent'
                      />
                      <span className='sr-only'>{t('documents.modal.generatingPreview')}</span>
                      <p className='text-sm text-gray-600 font-medium truncate max-w-full'>{selectedFile.name}</p>
                    </div>
                  ) : (
                    <div className='flex flex-col items-center gap-2 py-8'>
                      <FileText size={48} className='text-gray-400' />
                      <p className='text-sm text-gray-600 font-medium truncate max-w-full'>{selectedFile.name}</p>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className='flex flex-col gap-3'>
                  <button
                    type='button'
                    onClick={() => void handleConfirmUpload()}
                    className='flex items-center justify-center gap-2 w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition'>
                    <FileUp size={20} />
                    {t('documents.upload.confirmAndUpload')}
                  </button>
                  <button
                    type='button'
                    onClick={handleChooseAnother}
                    className='flex items-center justify-center gap-2 w-full py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition'>
                    {t('documents.modal.chooseAnother')}
                  </button>
                </div>
              </>
            )}

            {modalState === 'uploading' && selectedFile && (
              <UploadProgress
                fileName={selectedFile.name}
                progress={uploadProgress}
                phase={uploadPhase}
                error={uploadError}
                onCancel={handleCancelUpload}
                onRetry={handleRetryUpload}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
